import { defineStore } from 'pinia'
import { ref, watchEffect } from 'vue'
import type { AuthType, PassportPermission, PassportPermissionFlag, PermissionFlag, TokenType } from '@/entities/Auth'
import { useLoadAuth } from '@/entities/Auth'
import { useTranslate } from '@/shared/lib'
import { useLocale } from 'vuetify'

export const useAuth = defineStore('auth', () => {
  const user = ref<AuthType | null>(null)
  const token = ref<TokenType | null>(localStorage.getItem('token') || null)
  const currentPage = ref<string | null>(localStorage.getItem('current_page') || null)

  /**
   * Функция установки токена
   * @param value
   */
  const setToken = (value: TokenType) => {
    token.value = value
    localStorage.setItem('token', value)
  }

  /**
   * Функция установки текущей страницы
   * @param value
   */
  const setCurrentPage = (value: string) => {
    if (value.includes('auth')) return

    currentPage.value = value
    localStorage.setItem('current_page', value)
  }

  /**
   * Функция удаления токена
   */
  const deleteToken = () => {
    token.value = null
    localStorage.removeItem('token')
  }

  /**
   * Функция удаления авторизованного пользователя
   */
  const deleteAuth = () => {
    user.value = null
  }

  /**
   * Функция загрузки авторизованного пользователя
   */
  const loadAuth = () => {
    const result = useLoadAuth()
    const { setLocale } = useTranslate()
    const { current } = useLocale()

    watchEffect(() => {
      const { data, isSuccess } = result

      if (isSuccess.value && data.value) {
        user.value = data.value.data
        setLocale(user.value?.lang)

        current.value = user.value?.lang as string
      }
    })

    return result
  }

  /**
   * Функция проверки прав
   * @param permissions
   */
  const isPermissions = (permissions: string[]) => {
    if (!user.value) return false
    if (!permissions.length) return true

    return user.value.role.permissions.some((item) => permissions.includes(item))
  }

  /**
   * Функция проверки прав на просмотр вкладок
   * @param tab
   */
  const isTab = (tab: string) => {
    if (!user.value) return false

    return user.value.tabs.includes(tab)
  }

  /**
   * Функция проверки прав на тип объекта
   * @param objectTypeId
   * @param action
   */
  const hasTypePermission = (objectTypeId: number, action: PermissionFlag) => {
    if (!user.value) return false
    const perms = user.value.type_permissions || []
    const found = perms.find((p) => p.object_type_id === objectTypeId)
    // Если для типа не заданы права — поведение по умолчанию (полный доступ)
    if (!found) return true
    return Boolean(found[action])
  }

  /**
   * Функция проверки прав на элементы инвентаризации
   * @param objectTypeId
   * @param action
   */
  const hasElementPermission = (objectTypeId: number, action: PermissionFlag) => {
    if (!user.value) return false
    const perms = user.value.element_permissions || []
    const found = perms.find((p) => p.object_type_id === objectTypeId)
    // Если для типа не заданы права — поведение по умолчанию (полный доступ)
    if (!found) return true
    return Boolean(found[action])
  }

  /**
   * Функция проверки прав на «Подробный вид» паспорта по типу объекта
   * @param objectTypeId
   * @param action
   */
  const hasPassportPermission = (objectTypeId: number, action: PassportPermissionFlag) => {
    if (!user.value) return false
    const perms = user.value.passport_permissions || []
    const found = perms.find((p) => p.object_type_id === objectTypeId)
    // Если для типа не заданы права — поведение по умолчанию (полный доступ)
    if (!found) return true
    const legacy = (found as PassportPermission & { detailed_view?: boolean }).detailed_view
    if (legacy !== undefined && !found.read && !found.update) {
      return Boolean(legacy)
    }
    return Boolean(found[action])
  }

  /** Просмотр drawer «Подробный вид». */
  const hasPassportDetailedViewPermission = (objectTypeId: number) =>
    hasPassportPermission(objectTypeId, 'read')

  const canPassportDetailedViewForTypes = (typeIds: number[]) => {
    if (!user.value) return false
    const ids = typeIds.filter((id) => id > 0)
    if (!ids.length) return false
    return ids.every((id) => hasPassportDetailedViewPermission(id))
  }

  const canPassportUpdateDetailedViewForTypes = (typeIds: number[]) => {
    if (!user.value) return false
    const ids = typeIds.filter((id) => id > 0)
    if (!ids.length) return false
    return ids.every((id) => hasPassportPermission(id, 'update'))
  }

  /**
   * Функция проверки прав на отображение слоев на карте
   * @param permission
   */
  const hasMapLayerPermission = (permission: string) => {
    if (!user.value) return false

    return user.value.map_layers_permissions.includes(permission)
  }

  return {
    user,
    token,
    currentPage,

    setToken,
    setCurrentPage,
    deleteToken,
    deleteAuth,
    loadAuth,
    isTab,
    isPermissions,
    hasTypePermission,
    hasElementPermission,
    hasPassportPermission,
    hasPassportDetailedViewPermission,
    canPassportDetailedViewForTypes,
    canPassportUpdateDetailedViewForTypes,
    hasMapLayerPermission,
  }
})
